/**
 * Site Form Utilities
 * Shared validation logic and constants for Add/Edit Site modals
 * This reduces code duplication and ensures consistency
 */

// UK Postcode validation regex
export const UK_POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

// Site address is always UK
export const SITE_COUNTRY = 'United Kingdom';

// World countries list for client address (sorted alphabetically, UK first)
export const WORLD_COUNTRIES = [
  'United Kingdom',
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cape Verde',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo',
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Vatican City',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe'
];

/**
 * Validate UK postcode format
 * @param {string} postcode - The postcode to validate
 * @returns {boolean} - True if valid UK postcode format
 */
export const validatePostcode = (postcode) => {
  if (!postcode || !postcode.trim()) return false;
  return UK_POSTCODE_REGEX.test(postcode.trim());
};

/**
 * Build address string for comparison/snapshot
 * @param {object} site - Site object with address fields
 * @returns {string} - Concatenated address string
 */
export const getAddressString = (site) => {
  return `${site.address_line_1 || ''}|${site.address_line_2 || ''}|${site.city || ''}|${site.postcode || ''}`;
};

/**
 * Get full formatted address for display
 * @param {object} site - Site object with address fields
 * @returns {string} - Formatted address string
 */
export const getFullAddress = (site) => {
  const parts = [
    site.address_line_1,
    site.address_line_2,
    site.city,
    site.postcode,
    site.country || SITE_COUNTRY
  ];
  return parts.filter(p => p && p.trim()).join(', ');
};

/**
 * Check if all required address fields are filled with valid data
 * @param {object} site - Site object with address fields
 * @returns {boolean} - True if all required fields are valid
 */
export const areRequiredAddressFieldsFilled = (site) => {
  return (
    site.address_line_1?.trim() !== '' &&
    site.city?.trim() !== '' &&
    site.postcode?.trim() !== '' &&
    validatePostcode(site.postcode)
  );
};

/**
 * Default empty site object for form initialization
 */
export const getEmptySite = () => ({
  site_id: '',
  site_name: '',
  description: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  postcode: '',
  country: SITE_COUNTRY,
  status: 'active',
  client_id: null
});

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid email format
 */
export const validateEmail = (email) => {
  if (!email || !email.trim()) return true; // Optional field
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

/**
 * Validate UK phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid UK phone format
 */
export const validatePhone = (phone) => {
  if (!phone || !phone.trim()) return true; // Optional field
  const cleanedPhone = phone.replace(/[\s\-\(\)]/g, '');
  return /^(\+44|0)\d{10,11}$/.test(cleanedPhone);
};

/**
 * Geocoding error messages
 */
export const GEOCODE_ERRORS = {
  400: 'The address provided is invalid. Please check and try again.',
  404: 'Unable to find coordinates for this address. Please verify the address is correct.',
  429: 'Too many requests. Please wait a moment before trying again.',
  503: 'Geocoding service is temporarily unavailable. Please try again later.',
  default: 'An error occurred while calculating coordinates. Please try again.'
};

/**
 * Get user-friendly error message for geocoding errors
 * @param {object} error - Axios error object
 * @returns {string} - User-friendly error message
 */
export const getGeocodeErrorMessage = (error) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  
  if (detail) return detail;
  return GEOCODE_ERRORS[status] || GEOCODE_ERRORS.default;
};
